import { useCallback, useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { toast } from 'sonner';
import { Check, X, Pencil, Loader2, ClipboardCheck, Clock, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const AtlasApprovalsList = ({ embedded = false, onCountChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState({}); // approval_id -> {field: value}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/v1/atlas/pending-approvals`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setRows(d.approvals || []);
        onCountChange?.((d.approvals || []).length);
      }
    } catch { toast.error('Failed to load approvals'); }
    setLoading(false);
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id, edited = null) => {
    setBusyId(id);
    try {
      const url = edited
        ? `${API_URL}/api/v1/atlas/approvals/${id}/edit`
        : `${API_URL}/api/v1/atlas/approvals/${id}/approve`;
      const opts = edited
        ? { method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify({ tool_input: edited }) }
        : { method: 'POST', headers: auth() };
      const r = await fetch(url, opts);
      if (r.ok) { toast.success('Action executed'); load(); setEditing({}); }
      else { const e = await r.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    setBusyId(null);
  };

  const reject = async (id) => {
    const reason = prompt('Reason for rejection (optional):') || '';
    setBusyId(id);
    try {
      const r = await fetch(`${API_URL}/api/v1/atlas/approvals/${id}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST', headers: auth(),
      });
      if (r.ok) { toast.success('Rejected'); load(); }
      else toast.error('Failed to reject');
    } catch { toast.error('Network error'); }
    setBusyId(null);
  };

  if (loading) {
    const loadingNode = <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#0D7C8F]" /></div>;
    return embedded ? loadingNode : (
      <MainLayout title="Pending Approvals" subtitle="Actions waiting on your decision">
        {loadingNode}
      </MainLayout>
    );
  }

  const content = (
      <div className={`space-y-4 ${embedded ? 'p-4' : ''}`} data-testid="atlas-approvals-page">
        {rows.length === 0 && (
          <div className="rounded-xl border border-slate-700/50 bg-slate-900/40 p-12 text-center">
            <ClipboardCheck className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No pending actions. Nice.</p>
          </div>
        )}
        {rows.map(a => {
          const isEditing = !!editing[a.id];
          const editedBody = editing[a.id]?.body ?? a.tool_input?.body;
          const editedSubject = editing[a.id]?.subject ?? a.tool_input?.subject;
          return (
            <div key={a.id} data-testid={`approval-${a.id}`} className="rounded-xl border border-slate-700/50 bg-slate-900/60 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-700/50 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-4 h-4 text-[#f6be39]" />
                  <div>
                    <div className="text-sm font-bold text-white">{a.tool_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">action: {a.action_type}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <Clock className="w-3 h-3" />
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
              <div className="p-5 space-y-3">
                {a.tool_name === 'send_email_co' ? (
                  <>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">To</label>
                      <div className="text-sm text-slate-300 font-mono">{a.tool_input?.to}</div>
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Subject</label>
                      {isEditing ? (
                        <input
                          value={editedSubject || ''}
                          onChange={e => setEditing({ ...editing, [a.id]: { ...editing[a.id], subject: e.target.value } })}
                          className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                        />
                      ) : (
                        <div className="text-sm text-slate-300">{a.tool_input?.subject}</div>
                      )}
                    </div>
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Body</label>
                      {isEditing ? (
                        <textarea
                          rows={8}
                          value={editedBody || ''}
                          onChange={e => setEditing({ ...editing, [a.id]: { ...editing[a.id], body: e.target.value } })}
                          className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-slate-200 font-mono"
                        />
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm text-slate-300 bg-slate-950/50 rounded p-3 border border-slate-800/50">{a.tool_input?.body}</pre>
                      )}
                    </div>
                  </>
                ) : (
                  <pre className="whitespace-pre-wrap text-xs text-slate-400 bg-slate-950/50 rounded p-3 border border-slate-800/50 font-mono">{a.preview || JSON.stringify(a.tool_input, null, 2)}</pre>
                )}
              </div>
              <div className="px-5 py-3 border-t border-slate-700/50 bg-slate-950/40 flex items-center justify-end gap-2 flex-wrap">
                {!isEditing && (
                  <button
                    onClick={() => setEditing({ ...editing, [a.id]: { ...a.tool_input } })}
                    className="inline-flex items-center gap-1 text-xs font-bold text-slate-300 border border-slate-700 rounded-full px-3 py-1.5 hover:bg-white/5"
                    data-testid={`edit-${a.id}`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                )}
                {isEditing && (
                  <button
                    onClick={() => setEditing({ ...editing, [a.id]: undefined })}
                    className="text-xs font-bold text-slate-500 px-3 py-1.5"
                  >
                    Cancel edit
                  </button>
                )}
                <button
                  onClick={() => reject(a.id)}
                  disabled={busyId === a.id}
                  data-testid={`reject-${a.id}`}
                  className="inline-flex items-center gap-1 text-xs font-bold text-red-300 border border-red-500/40 rounded-full px-3 py-1.5 hover:bg-red-500/10 disabled:opacity-40"
                >
                  <X className="w-3 h-3" /> Reject
                </button>
                <button
                  onClick={() => approve(a.id, isEditing ? editing[a.id] : null)}
                  disabled={busyId === a.id}
                  data-testid={`approve-${a.id}`}
                  className="inline-flex items-center gap-1 text-xs font-bold bg-[#0D7C8F] hover:bg-[#006879] text-white rounded-full px-3 py-1.5 disabled:opacity-40"
                >
                  {busyId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  {isEditing ? 'Approve with edits' : 'Approve'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
  );

  if (embedded) return content;
  return <MainLayout title="Atlas · Pending Approvals" subtitle={`${rows.length} action${rows.length === 1 ? '' : 's'} waiting on you`}>{content}</MainLayout>;
};

const AtlasApprovalsPage = () => <AtlasApprovalsList />;

export default AtlasApprovalsPage;
